import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    cardClickableMixin,
    cls,
    defaultBorderMixin,
    FilterChip,
    TextField,
    Typography
} from "@rebasepro/ui";
import { client } from "../lib/rebaseClient";
import { describeError, FREEMAIL, inboxApi, type Company, type Message, type Person, type Thread } from "./shared";

/**
 * "Who is this conversation with?" — answered by hand.
 *
 * A link made here is `linked_by: "manual"`, which the sync never overrules: a
 * correction the next poll undoes is worse than no correction, because it
 * teaches people to stop making them.
 *
 * The sender can also become the record on the spot — a person from their
 * address and name, or a company from their domain (never from a free-mail
 * domain: somebody at gmail.com is not a company called Gmail).
 */

type Kind = "person" | "company";

interface Choice { id: string; label: string; detail: string }

export function ThreadLinkPicker({
    thread,
    messages,
    onLinked,
    onCancel
}: {
    thread: Thread;
    messages: Message[];
    onLinked: (message: string) => void | Promise<void>;
    onCancel: () => void;
}) {
    const sender = useMemo(() => {
        const first = messages.find(m => m.direction === "inbound");
        const email = (first?.from_email ?? thread.participants?.[0] ?? "").toLowerCase();
        return { email, name: first?.from_name?.trim() || "" };
    }, [messages, thread.participants]);
    const domain = sender.email.split("@")[1] ?? "";
    const corporate = Boolean(domain) && !FREEMAIL.has(domain);

    const [kind, setKind] = useState<Kind>("person");
    const [query, setQuery] = useState(sender.name || sender.email.split("@")[0] || "");
    const [results, setResults] = useState<Choice[]>([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [senderKnown, setSenderKnown] = useState(false);
    const [domainKnown, setDomainKnown] = useState(false);

    useEffect(() => { setQuery(kind === "company" ? domain.split(".")[0] ?? "" : sender.name || sender.email.split("@")[0] || ""); }, [kind, domain, sender]);

    // Whether the sender is already somebody, so "create" is not offered twice.
    useEffect(() => {
        if (!sender.email) return;
        client.data.collection<Person>("prospects").find({ where: { email: ["==", sender.email] } as never, limit: 1 })
            .then(({ data }) => setSenderKnown((data ?? []).length > 0))
            .catch(() => setSenderKnown(false));
    }, [sender.email]);

    // …and whether their company already is, so "add" is not offered for it.
    useEffect(() => {
        if (!corporate) return;
        client.data.collection<Company>("companies").find({ where: { domain: ["==", domain] } as never, limit: 1 })
            .then(({ data }) => setDomainKnown((data ?? []).length > 0))
            .catch(() => setDomainKnown(false));
    }, [corporate, domain]);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        let live = true;
        setSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const like = `%${q}%`;
                const found = new Map<string, Choice>();
                if (kind === "person") {
                    for (const field of ["display_name", "email", "handle"]) {
                        const { data } = await client.data.collection<Person>("prospects").find({ where: { [field]: ["ilike", like] } as never, limit: 8 });
                        for (const p of data ?? []) {
                            found.set(p.id, { id: p.id, label: p.display_name || p.handle || p.email || "(unnamed)", detail: [p.email, p.role, p.company].filter(Boolean).join(" · ") });
                        }
                    }
                } else {
                    for (const field of ["name", "domain"]) {
                        const { data } = await client.data.collection<Company>("companies").find({ where: { [field]: ["ilike", like] } as never, limit: 8 });
                        for (const company of data ?? []) {
                            found.set(company.id, { id: company.id, label: company.name || company.domain || "(unnamed)", detail: company.domain ?? "" });
                        }
                    }
                }
                if (live) setResults([...found.values()].slice(0, 8));
            } catch (err) {
                if (live) setError(describeError(err));
            } finally {
                if (live) setSearching(false);
            }
        }, 250);
        return () => { live = false; window.clearTimeout(timer); };
    }, [query, kind]);

    const link = async (choice: { prospect?: string | null; company?: string | null }, message: string) => {
        setBusy(true);
        setError(undefined);
        try {
            await inboxApi.link(thread.id, {
                prospect: choice.prospect !== undefined ? choice.prospect : thread.prospectId ?? null,
                company: choice.company !== undefined ? choice.company : thread.companyId ?? null
            });
            await onLinked(message);
        } catch (err) {
            setError(describeError(err));
        } finally {
            setBusy(false);
        }
    };

    const createPerson = async () => {
        setBusy(true);
        setError(undefined);
        try {
            const created = await client.data.collection("prospects").create({
                handle: `email:${sender.email}`,
                display_name: sender.name || sender.email.split("@")[0],
                email: sender.email,
                email_source: "manual",
                // They wrote to us: the conversation is already under way.
                status: "replied"
            }) as { id?: string; data?: { id?: string } };
            const id = created?.id ?? created?.data?.id;
            if (!id) throw new Error("The person could not be created.");
            await link({ prospect: String(id) }, `${sender.name || sender.email} added to People and linked`);
        } catch (err) {
            setError(describeError(err));
            setBusy(false);
        }
    };

    const createCompany = async () => {
        setBusy(true);
        setError(undefined);
        try {
            const name = domain.split(".")[0] ?? domain;
            const created = await client.data.collection("companies").create({
                name: name.charAt(0).toUpperCase() + name.slice(1),
                domain,
                website: `https://${domain}`,
                source: "manual",
                status: "contacted"
            }) as { id?: string; data?: { id?: string } };
            const id = created?.id ?? created?.data?.id;
            if (!id) throw new Error("The company could not be created.");
            await link({ company: String(id) }, `${domain} added to Companies and linked`);
        } catch (err) {
            setError(describeError(err));
            setBusy(false);
        }
    };

    const linked = Boolean(thread.prospectId || thread.companyId);

    return (
        <Card className={cls("p-3 flex flex-col gap-3", defaultBorderMixin)}>
            <Typography variant="subtitle2">Who is this conversation with?</Typography>
            <div className="flex gap-1.5">
                <FilterChip active={kind === "person"} onClick={() => setKind("person")}>A person</FilterChip>
                <FilterChip active={kind === "company"} onClick={() => setKind("company")}>A company</FilterChip>
            </div>

            {kind === "person" && sender.email && !senderKnown && (
                <Card className={cls("p-3 flex items-center gap-3 flex-wrap", defaultBorderMixin)}>
                    <div className="grow min-w-0">
                        <Typography variant="body2" className="font-medium">Add {sender.name || sender.email} to People</Typography>
                        <Typography variant="caption" color="secondary">{sender.email} — linked to this conversation straight away</Typography>
                    </div>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => void createPerson()}>Add and link</Button>
                </Card>
            )}
            {kind === "company" && corporate && !domainKnown && (
                <Card className={cls("p-3 flex items-center gap-3 flex-wrap", defaultBorderMixin)}>
                    <div className="grow min-w-0">
                        <Typography variant="body2" className="font-medium">Not in Companies yet? They wrote from {domain}.</Typography>
                    </div>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => void createCompany()}>Add {domain} and link</Button>
                </Card>
            )}

            <TextField
                size="small"
                label={kind === "person" ? "Search People" : "Search Companies"}
                value={query}
                onChange={event => setQuery(event.target.value)}
            />
            {query.trim().length < 2 && <Typography variant="caption" color="secondary">Type at least two letters.</Typography>}
            {searching && <Typography variant="caption" color="secondary">Searching…</Typography>}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
                <Typography variant="caption" color="secondary">Nobody by that name.</Typography>
            )}
            <div className="flex flex-col gap-1.5">
                {results.map(choice => (
                    <Card
                        key={choice.id}
                        className={cls("p-2.5 cursor-pointer", cardClickableMixin, defaultBorderMixin)}
                        onClick={() => void link(kind === "person" ? { prospect: choice.id } : { company: choice.id }, `Linked to ${choice.label}`)}
                    >
                        <Typography variant="body2" className="font-medium">{choice.label}</Typography>
                        {choice.detail && <Typography variant="caption" color="secondary">{choice.detail}</Typography>}
                    </Card>
                ))}
            </div>

            {error && <Alert color="error" size="small">{error}</Alert>}

            <div className="flex gap-2">
                <Button size="small" variant="text" onClick={onCancel} disabled={busy}>Cancel</Button>
                {linked && (
                    <Button size="small" variant="text" disabled={busy} onClick={() => void link({ prospect: null, company: null }, "Unlinked — the sync may link it again")}>
                        Unlink
                    </Button>
                )}
            </div>
        </Card>
    );
}
